// Shared "API Access" panel - used from both UserProfileModal and the
// Import Data page so the two stay in sync instead of drifting.
//
// The raw key is only ever known once, right after generate/rotate (backend
// stores only its sha256 hash - see ClientApiKey/rotateApiKey in
// potholecrm-backend). On every later visit this shows the masked status
// from GET /customers/apiKey/status (prefix + created date) instead of
// nothing, so "did I already have a key?" doesn't require guessing.
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Button, Alert, Space, Input, notification } from "antd";
import dayjs from "dayjs";
import { BACKEND_URL, CLIENT_API_DOCS_URL } from "../../config/config";

export default function ApiKeyPanel({ contactId }) {
  const [status, setStatus] = useState(null);
  const [isLoadingStatus, setIsLoadingStatus] = useState(false);
  const [newApiKey, setNewApiKey] = useState(null);
  const [isRotating, setIsRotating] = useState(false);

  const loadStatus = async () => {
    if (!contactId) return;
    setIsLoadingStatus(true);
    const response = await axios
      .get(`${BACKEND_URL}/customers/apiKey/status`, { params: { contact_id: contactId } })
      .catch(() => null);
    setIsLoadingStatus(false);
    if (response?.data) setStatus(response.data);
  };

  useEffect(() => {
    loadStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactId]);

  const rotateApiKey = async () => {
    setIsRotating(true);
    const response = await axios
      .post(`${BACKEND_URL}/customers/apiKey/rotate`, { contact_id: contactId })
      .catch((err) => {
        notification.error({
          message: err?.response?.data?.message || "Unable to generate an API key.",
        });
        return null;
      });
    setIsRotating(false);

    if (response?.data?.apiKey) {
      setNewApiKey(response.data.apiKey);
      setStatus({
        hasKey: true,
        keyPrefix: response.data.keyPrefix,
        createdAt: response.data.createdAt,
      });
    }
  };

  const copyApiKey = () => {
    if (!newApiKey) return;
    navigator.clipboard?.writeText(newApiKey).then(
      () => notification.success({ message: "API key copied to clipboard." }),
      () => notification.error({ message: "Could not copy - select and copy manually." })
    );
  };

  return (
    <div>
      <h4 style={{ marginBottom: 4 }}>API Access</h4>
      <p style={{ color: "#888", fontSize: 12, marginBottom: 8 }}>
        Only needed if your own systems will call our{" "}
        <a href={CLIENT_API_DOCS_URL} target="_blank" rel="noreferrer">API</a>{" "}
        directly. Uploading a CSV from this page never requires a key.
        Generating a new key immediately revokes any previous one - only one
        is ever active at a time.
      </p>

      {newApiKey ? (
        <>
          <Alert
            type="warning"
            showIcon
            message="Copy this key now - it will not be shown again."
            style={{ marginBottom: 8 }}
          />
          <Space.Compact style={{ display: "flex", marginBottom: 8 }}>
            <Input value={newApiKey} readOnly style={{ flex: 1 }} />
            <Button onClick={copyApiKey}>Copy</Button>
          </Space.Compact>
        </>
      ) : !isLoadingStatus && status?.hasKey ? (
        <p style={{ fontSize: 12, marginBottom: 8 }}>
          Active key: <code>{status.keyPrefix}••••••••</code>
          {status.createdAt ? ` - created ${dayjs(status.createdAt).format("MM/DD/YYYY")}` : ""}
        </p>
      ) : !isLoadingStatus ? (
        <p style={{ fontSize: 12, marginBottom: 8, color: "#888" }}>No API key yet.</p>
      ) : null}

      <Button onClick={rotateApiKey} loading={isRotating}>
        {status?.hasKey ? "Regenerate Key (revokes the current one)" : "Generate API Key"}
      </Button>
    </div>
  );
}
